import { ASSAY_BASES, AssayBasis } from '@/models/Inventory';

/**
 * Client-side mirror of the server's ActivityFactorService, used for the live previews on the lot
 * forms and the ingredient editor. The server remains the source of truth for what is persisted on
 * a batch; this exists so the number a user sees while typing is the number the batch will use.
 * Keep the rules here in step with the C# service:
 *
 *   factor = assay / 100, then × (100 − LOD) / 100 on an Anhydrous or Dried basis;
 *   adjusted = scaled / factor; a fixed conversion factor replaces the COA (adjusted = scaled × fixed).
 *
 * The hard plausibility guards are mirrored as well, and are the point of the previews: the server
 * silently falls back to a factor of 1.0 for an implausible value, so a preview that accepted one
 * would show a believable factor for exactly the typo the guard exists to catch.
 */

export interface CoaRangeBounds {
    assayMin: number | null;
    assayMax: number | null;
    lodMin: number | null;
    lodMax: number | null;
}

export const UNBOUNDED_COA_RANGE: CoaRangeBounds = { assayMin: null, assayMax: null, lodMin: null, lodMax: null };

export type CoaRangeWarning = 'AssayBelowMinimum' | 'AssayAboveMaximum' | 'LodBelowMinimum' | 'LodAboveMaximum';

/** Which input produced the factor. */
export type ActivityFactorSource = 'None' | 'Coa' | 'FixedFactor';

/** Why a flagged ingredient fell back to a factor of 1.0; anything but 'None' is a missing-COA condition. */
export type ActivityFactorFallback = 'None' | 'NoCoa' | 'UnusableCoa' | 'UnusableFixedFactor';

/** Why a COA that is complete still cannot be used, so the forms can say which value to correct. */
export type CoaUnusableReason = 'AssayImplausible' | 'LodImplausible';

/** The COA triple as the forms hold it — number inputs may hand back '' or NaN, hence the loose types. */
export interface CoaValues {
    assayPercent: number | string | null | undefined;
    assayBasis: string | null | undefined;
    lodPercent: number | string | null | undefined;
}

export interface ActivityFactorInput extends Partial<CoaValues> {
    /** Formula quantity already scaled to the batch. */
    scaledQuantity: number;
    /** The ingredient's flag, already combined with the client feature toggle by the caller. */
    applyActivityFactor: boolean;
    fixedConversionFactor?: number | string | null;
    overrideQuantity?: number | null;
}

export interface ActivityFactorResult {
    scaledQuantity: number;
    activityFactor: number;
    adjustedQuantity: number;
    effectiveQuantity: number;
    factorSource: ActivityFactorSource;
    fallback: ActivityFactorFallback;
    lodAssumedZero: boolean;
    rangeWarnings: CoaRangeWarning[];
    /** Flagged but no usable factor could be derived, so 1.0 was applied. */
    missingCoa: boolean;
    /** A fixed factor was used, so whatever COA the lot carries was not applied. */
    coaNotApplied: boolean;
}

/** What the lot forms show for a COA on its own: the factor the lot would yield, and why it might not. */
export interface CoaFactorPreview {
    /** Null when the COA cannot yield a factor (incomplete or unusable). */
    factor: number | null;
    /** True once assay and a valid basis are both present. */
    hasCoa: boolean;
    /** The COA is complete but cannot yield a valid factor: an implausible assay, or an LOD outside 0–100 on an LOD basis. */
    unusable: boolean;
    /** Which value made it unusable; null while it is usable or still incomplete. */
    unusableReason: CoaUnusableReason | null;
    lodApplies: boolean;
    lodAssumedZero: boolean;
    rangeWarnings: CoaRangeWarning[];
}

const HUNDRED = 100;

// Hard plausibility limits, mirrored from ActivityFactorService and deliberately separate from the
// configurable CoaRangeBounds. Those are advisory — a range warning never changes the factor. These
// decide whether a value is usable at all: outside them the server applies a factor of 1.0 and flags
// the batch for review. A configured bound can widen the assay band but never narrow it — see
// assayHardBand.

/**
 * Below this an assay is a fraction entered where a percentage was meant (1.003 for 100.3), which
 * divides by a hundredth and multiplies the weigh-out by 100.
 */
export const ASSAY_FLOOR_PERCENT = 50;

/** Above this an assay is a transcription error, not a purity. */
export const ASSAY_CEILING_PERCENT = 150;

/**
 * The band an assay must fall in to be usable, widened by a configured bound already outside it.
 * Mirrors ActivityFactorService.AssayHardBand.
 *
 * Widening only, because the configured bounds are warning thresholds and their default is 90–110:
 * reading a configured minimum as the hard floor would make a 75% assay unusable on a default
 * configuration, and a warning must never change the factor. Configuring a minimum below 50 is a
 * client stating that assays that low are normal for their materials — a dilution, or a liquid
 * concentrate assayed w/w. A blank bound keeps the hard floor: blank disables the warning, not the
 * plausibility check.
 *
 * Widening stops short of zero, which is why previewCoaFactor rejects a non-positive assay
 * independently of this band rather than relying on the floor alone: the assay is the divisor, so a
 * configured minimum of 0 admits an assay of 0 and a negative one admits a negative assay. A lot of
 * zero potency is not weighable at any quantity, so only a positive configured floor is honoured.
 */
export function assayHardBand(bounds: CoaRangeBounds): { floor: number; ceiling: number } {
    return {
        floor: Math.min(ASSAY_FLOOR_PERCENT, bounds.assayMin ?? ASSAY_FLOOR_PERCENT),
        ceiling: Math.max(ASSAY_CEILING_PERCENT, bounds.assayMax ?? ASSAY_CEILING_PERCENT),
    };
}

/**
 * A fixed conversion factor corrects between forms (salt to base, hydrate to anhydrous). Outside
 * this band it is a misplaced decimal point: 1033 for 1.033 weighs out a thousandfold.
 */
export const FIXED_FACTOR_FLOOR = 0.5;
export const FIXED_FACTOR_CEILING = 2;

/**
 * The canonical spelling of a stored basis, or null when it is not one of the three. Trimmed and
 * case-insensitive, mirroring AssayBasis.Canonical: CK_Inventory_AssayBasis is evaluated under a
 * case-insensitive collation and ignores a trailing space, and only bulk receiving canonicalizes
 * what it stores, so 'anhydrous' and 'Anhydrous ' are both spellings a lot can carry. Matching
 * strictly here would preview — and post — an unadjusted weigh-out for a lot the server reads as
 * fully documented, so nothing would flag the batch for review.
 */
export function canonicalAssayBasis(basis: string | null | undefined): AssayBasis | null {
    if (basis === null || basis === undefined) return null;
    const trimmed = basis.trim().toLowerCase();
    return ASSAY_BASES.find(candidate => candidate.toLowerCase() === trimmed) ?? null;
}

export function isValidAssayBasis(basis: string | null | undefined): boolean {
    return canonicalAssayBasis(basis) !== null;
}

/** LOD participates only on the bases that state the assay excludes water. */
export function assayBasisAppliesLod(basis: string | null | undefined): boolean {
    const canonical = canonicalAssayBasis(basis);
    return canonical === 'Anhydrous' || canonical === 'Dried';
}

/** Coerces what a number input hands back ('' while blank, NaN when unparsable) to a number or null. */
export function toNumberOrNull(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}

/** True when the triple counts as a COA: assay and a valid basis; LOD alone is not a COA. */
export function hasCoa(coa: CoaValues): boolean {
    return toNumberOrNull(coa.assayPercent) !== null && isValidAssayBasis(coa.assayBasis);
}

/** True when any of the three COA fields carries a value. */
export function hasAnyCoaValue(coa: CoaValues): boolean {
    return toNumberOrNull(coa.assayPercent) !== null
        || (coa.assayBasis !== null && coa.assayBasis !== undefined && coa.assayBasis !== '')
        || toNumberOrNull(coa.lodPercent) !== null;
}

/**
 * Inclusive range check; a null bound means no check on that side. Callers pass null for the LOD
 * when it did not participate (As-Is basis, or assumed zero) so it is not judged.
 */
export function checkCoaRange(assayPercent: number | null, lodPercent: number | null, bounds: CoaRangeBounds): CoaRangeWarning[] {
    const warnings: CoaRangeWarning[] = [];

    if (assayPercent !== null) {
        if (bounds.assayMin !== null && assayPercent < bounds.assayMin) warnings.push('AssayBelowMinimum');
        if (bounds.assayMax !== null && assayPercent > bounds.assayMax) warnings.push('AssayAboveMaximum');
    }

    if (lodPercent !== null) {
        if (bounds.lodMin !== null && lodPercent < bounds.lodMin) warnings.push('LodBelowMinimum');
        if (bounds.lodMax !== null && lodPercent > bounds.lodMax) warnings.push('LodAboveMaximum');
    }

    return warnings;
}

/** The factor a COA yields on its own, for the read-only preview on the lot forms. */
export function previewCoaFactor(coa: CoaValues, bounds: CoaRangeBounds = UNBOUNDED_COA_RANGE): CoaFactorPreview {
    const assay = toNumberOrNull(coa.assayPercent);
    const lodApplies = assayBasisAppliesLod(coa.assayBasis);
    const suppliedLod = toNumberOrNull(coa.lodPercent);

    if (assay === null || !isValidAssayBasis(coa.assayBasis)) {
        return { factor: null, hasCoa: false, unusable: false, unusableReason: null, lodApplies, lodAssumedZero: false, rangeWarnings: [] };
    }

    const lodAssumedZero = lodApplies && suppliedLod === null;
    const lod = lodApplies ? (suppliedLod ?? 0) : 0;

    // Range warnings apply only to values that actually participated: an LOD that was assumed, or
    // that does not apply on an as-is basis, is not checked.
    const rangeWarnings = checkCoaRange(assay, lodApplies ? suppliedLod : null, bounds);

    // Both values are judged before they are combined, never afterwards. Multiplication is closed
    // over pairs of negatives, so a guard on the resulting factor cannot tell a clean COA from a
    // negative assay cancelling an LOD above 100: assay -50 with LOD 150 yields +0.25 and weighs out
    // four times the API. An LOD of exactly 100 leaves no API at all and would divide by zero.
    const unusable = (reason: CoaUnusableReason): CoaFactorPreview =>
        ({ factor: null, hasCoa: true, unusable: true, unusableReason: reason, lodApplies, lodAssumedZero, rangeWarnings });

    //
    // The non-positive test is separate from the band because a configured minimum widens the floor:
    // assayMin of 0 lowers it to 0, an assay of exactly 0 then clears "0 < 0", and the factor of 0
    // that follows divides to Infinity in calculateActivityFactor — which JSON.stringify serialises
    // as null, so the posted weigh-out arrives at the save procedure as NULL.
    const assayBand = assayHardBand(bounds);
    if (assay <= 0 || assay < assayBand.floor || assay > assayBand.ceiling) return unusable('AssayImplausible');
    if (lod < 0 || lod >= HUNDRED) return unusable('LodImplausible');

    let factor = assay / HUNDRED;
    if (lodApplies) factor *= (HUNDRED - lod) / HUNDRED;

    return { factor, hasCoa: true, unusable: false, unusableReason: null, lodApplies, lodAssumedZero, rangeWarnings };
}

/** True when a fixed conversion factor is present and inside the band the server will actually apply. */
export function isFixedFactorUsable(fixedConversionFactor: number | string | null | undefined): boolean {
    const fixed = toNumberOrNull(fixedConversionFactor);
    return fixed !== null && fixed >= FIXED_FACTOR_FLOOR && fixed <= FIXED_FACTOR_CEILING;
}

/** The activity factor a fixed conversion factor reports (its reciprocal), or null when it is not usable. */
export function fixedFactorActivityFactor(fixedConversionFactor: number | string | null | undefined): number | null {
    const fixed = toNumberOrNull(fixedConversionFactor);
    return fixed !== null && isFixedFactorUsable(fixed) ? 1 / fixed : null;
}

/** Full calculation for one ingredient on one lot; mirrors ActivityFactorService.Calculate. */
export function calculateActivityFactor(input: ActivityFactorInput, bounds: CoaRangeBounds = UNBOUNDED_COA_RANGE): ActivityFactorResult {
    // Unflagged ingredients are never adjusted, whatever the lot or the ingredient carries.
    if (!input.applyActivityFactor) return passthrough(input, 'None');

    // A fixed conversion factor replaces the COA outright — the two are never combined. Entered as a
    // multiplier, so adjusted = scaled × factor and the reported activity factor is its reciprocal.
    const fixed = toNumberOrNull(input.fixedConversionFactor);
    if (fixed !== null) {
        if (!isFixedFactorUsable(fixed)) return passthrough(input, 'UnusableFixedFactor');
        const adjusted = input.scaledQuantity * fixed;
        return {
            scaledQuantity: input.scaledQuantity,
            activityFactor: 1 / fixed,
            adjustedQuantity: adjusted,
            effectiveQuantity: input.overrideQuantity ?? adjusted,
            factorSource: 'FixedFactor',
            fallback: 'None',
            lodAssumedZero: false,
            rangeWarnings: [],
            missingCoa: false,
            coaNotApplied: true,
        };
    }

    const preview = previewCoaFactor(
        { assayPercent: input.assayPercent, assayBasis: input.assayBasis, lodPercent: input.lodPercent }, bounds);
    if (!preview.hasCoa) return passthrough(input, 'NoCoa');
    // Mirrors the service's guard on the computed factor. Unreachable once previewCoaFactor refuses a
    // non-positive assay, and kept because this division is the one whose result is posted: a factor
    // of 0 yields Infinity, which serialises to null and nulls out the lot's on-hand quantity when
    // the deduction subtracts it, and the Weigh-Out cell renders blank rather than showing anything
    // wrong beforehand.
    if (preview.unusable || preview.factor === null || preview.factor <= 0) return passthrough(input, 'UnusableCoa');

    const adjusted = input.scaledQuantity / preview.factor;
    return {
        scaledQuantity: input.scaledQuantity,
        activityFactor: preview.factor,
        adjustedQuantity: adjusted,
        effectiveQuantity: input.overrideQuantity ?? adjusted,
        factorSource: 'Coa',
        fallback: 'None',
        lodAssumedZero: preview.lodAssumedZero,
        rangeWarnings: preview.rangeWarnings,
        missingCoa: false,
        coaNotApplied: false,
    };
}

function passthrough(input: ActivityFactorInput, fallback: ActivityFactorFallback): ActivityFactorResult {
    return {
        scaledQuantity: input.scaledQuantity,
        activityFactor: 1,
        adjustedQuantity: input.scaledQuantity,
        effectiveQuantity: input.overrideQuantity ?? input.scaledQuantity,
        factorSource: 'None',
        fallback,
        lodAssumedZero: false,
        rangeWarnings: [],
        missingCoa: fallback !== 'None',
        // A fixed factor the server refused still means the lot's COA did not produce this result,
        // so it must not render as applied — a valid assay shown beside a factor it did not produce
        // is the misreading this flag exists to prevent.
        coaNotApplied: fallback === 'UnusableFixedFactor',
    };
}

/** Formats a factor for display at the configured precision; blank for null. */
export function formatActivityFactor(factor: number | null | undefined, decimalPlaces: number = 4): string {
    if (factor === null || factor === undefined || !Number.isFinite(factor)) return '';
    const places = Number.isInteger(decimalPlaces) && decimalPlaces >= 0 ? Math.min(decimalPlaces, 10) : 4;
    return factor.toFixed(places);
}

/**
 * Why the COA cannot be used, naming the value to correct. The bounds are quoted because the point
 * of the message is that the entered value is off by a decimal place, not merely rejected — so the
 * band quoted has to be the one that was actually applied, which a configured minimum can widen.
 */
export function describeUnusableCoa(reason: CoaUnusableReason, bounds: CoaRangeBounds = UNBOUNDED_COA_RANGE): string {
    switch (reason) {
        case 'AssayImplausible': {
            const band = assayHardBand(bounds);
            return `Assay must be between ${band.floor}% and ${band.ceiling}% — check for a misplaced decimal point. `
                + 'As entered it will be ignored and the lot treated as having no COA.';
        }
        case 'LodImplausible':
            return 'LOD / water content must be 0% or more and under 100%. '
                + 'As entered it will be ignored and the lot treated as having no COA.';
    }
}

/**
 * The band a fixed conversion factor has to fall in, on its own. Split out because the two screens
 * that reject a factor reject it differently: the formula editor refuses to save one at all, so
 * telling the user there that it will be ignored and the weigh-out left unadjusted would describe a
 * fallback that screen cannot reach.
 */
export function describeFixedFactorBand(): string {
    return `The conversion factor must be between ${FIXED_FACTOR_FLOOR} and ${FIXED_FACTOR_CEILING} — check for a misplaced decimal point.`;
}

/**
 * The band, plus what the server does with a factor outside it. For the fill flow, which reads a
 * factor already stored on the formula and so cannot refuse it — there the fallback is what happens.
 */
export function describeUnusableFixedFactor(): string {
    return `${describeFixedFactorBand()} As entered it will be ignored and the weigh-out left unadjusted.`;
}

/** Human-readable text for each range warning, quoting the bound that was crossed. */
export function describeCoaRangeWarnings(warnings: CoaRangeWarning[], bounds: CoaRangeBounds): string[] {
    return warnings.map(warning => {
        switch (warning) {
            case 'AssayBelowMinimum': return `Assay is below the acceptable minimum of ${bounds.assayMin}%.`;
            case 'AssayAboveMaximum': return `Assay is above the acceptable maximum of ${bounds.assayMax}%.`;
            case 'LodBelowMinimum': return `LOD / water content is below the acceptable minimum of ${bounds.lodMin}%.`;
            case 'LodAboveMaximum': return `LOD / water content is above the acceptable maximum of ${bounds.lodMax}%.`;
        }
    });
}

// ---- Steps linked to an ingredient ------------------------------------------------------------
// Mirror of the C# service's CalculateStepExpectedWeight(s) / ValidateStepLinks: a linked step
// weighs out a share of its ingredient's effective quantity, or — for the one remainder (QS) step —
// whatever the other linked steps leave. Same rules, same wording as the server.

/** One step linked to an ingredient, as the formula editor and the filling page hold it. */
export interface StepLink {
    /** Share of the effective quantity, as a percentage (50 = half). Ignored on the remainder step. */
    proportionPercent: number | string | null | undefined;
    /** This step weighs out the balance after the other linked steps. */
    isRemainder: boolean;
}

export interface StepLinkValidation {
    isValid: boolean;
    errors: string[];
}

export const STEP_LINK_VALID: StepLinkValidation = { isValid: true, errors: [] };

/**
 * A percentage as the database will hold it. ProportionPercent is DECIMAL(7,4) and SQL Server rounds
 * half away from zero on the way in, so this -- not what the user typed -- is the value the formula
 * will actually carry, and the value the procedure's HAVING clause sums.
 *
 * Validating the typed value instead let the three layers disagree: three steps of 33.333333 total
 * 99.999999, which rounded to 100 and passed here, then hit an exact comparison in ValidateStepLinks
 * and in usp_CompoundSteps_Save -- the page called the form valid and the save reported an error.
 * Rounding first also keeps 33.33 + 33.33 + 33.34 totalling 100 despite binary floating point.
 */
function toStoredPercent(value: number): number {
    return Math.sign(value) * Math.round(Math.abs(value) * 10000) / 10000;
}

export function calculateStepExpectedWeight(effectiveQuantity: number, proportionPercent: number): number {
    return effectiveQuantity * proportionPercent / 100;
}

/**
 * Expected weight of every linked step, in the order given. The remainder is the effective quantity
 * less the other steps' computed weights, so the linked steps always sum back to it exactly.
 */
export function calculateStepExpectedWeights(effectiveQuantity: number, steps: StepLink[]): number[] {
    const shareOf = (step: StepLink) => calculateStepExpectedWeight(effectiveQuantity, toNumberOrNull(step.proportionPercent) ?? 0);
    const proportionalTotal = steps.filter(step => !step.isRemainder).reduce((sum, step) => sum + shareOf(step), 0);
    return steps.map(step => step.isRemainder ? effectiveQuantity - proportionalTotal : shareOf(step));
}

/** Validates the set of steps linked to ONE ingredient; an empty set is valid. */
export function validateStepLinks(steps: StepLink[]): StepLinkValidation {
    if (steps.length === 0) return STEP_LINK_VALID;

    const errors: string[] = [];

    const remainderCount = steps.filter(step => step.isRemainder).length;
    if (remainderCount > 1) {
        errors.push('Only one linked step per ingredient can be the remainder step.');
    }

    // Both rules judge the stored value, because that is what the server will judge. A proportion of
    // 0.00001 stores as 0.0000 and the procedure rejects it; checking the typed value would let it
    // through here.
    const proportional = steps.filter(step => !step.isRemainder);
    const share = (step: StepLink) => toStoredPercent(toNumberOrNull(step.proportionPercent) ?? 0);
    if (proportional.some(step => share(step) <= 0)) {
        errors.push('Each linked step must carry a proportion greater than 0%, unless it is the remainder step.');
    }

    const total = toStoredPercent(proportional.reduce((sum, step) => sum + share(step), 0));
    if (remainderCount > 0) {
        if (total >= 100) {
            errors.push(`With a remainder step, the other linked steps must total under 100% (currently ${total}%).`);
        }
    } else if (total !== 100) {
        errors.push(`Linked step proportions must total exactly 100% (currently ${total}%).`);
    }

    return errors.length === 0 ? STEP_LINK_VALID : { isValid: false, errors };
}

/** A formula step's link, as the compound editor and filling page see it. */
export interface LinkedStepShape {
    stepNumber: number;
    compoundIngredientID: number | null | undefined;
    proportionPercent: number | string | null | undefined;
    isRemainderStep: boolean | null | undefined;
}

/**
 * Groups a formula's steps by linked ingredient and validates each group, returning one message per
 * problem that names the steps and the ingredient. Unlinked steps are never checked. Empty when the
 * whole set is valid.
 */
export function describeStepLinkProblems(steps: LinkedStepShape[], ingredientName: (compoundIngredientID: number) => string): string[] {
    const groups = new Map<number, LinkedStepShape[]>();
    for (const step of steps) {
        if (step.compoundIngredientID == null) continue;
        const group = groups.get(step.compoundIngredientID) ?? [];
        group.push(step);
        groups.set(step.compoundIngredientID, group);
    }

    const problems: string[] = [];
    const ordered = [...groups.entries()].sort((a, b) => Math.min(...a[1].map(s => s.stepNumber)) - Math.min(...b[1].map(s => s.stepNumber)));
    for (const [compoundIngredientID, group] of ordered) {
        const sorted = [...group].sort((a, b) => a.stepNumber - b.stepNumber);
        const validation = validateStepLinks(sorted.map(step => ({ proportionPercent: step.proportionPercent, isRemainder: !!step.isRemainderStep })));
        if (validation.isValid) continue;
        const label = sorted.length === 1 ? `Step ${sorted[0].stepNumber}` : `Steps ${sorted.map(step => step.stepNumber).join(', ')}`;
        problems.push(`${label} (${ingredientName(compoundIngredientID)}): ${validation.errors.join(' ')}`);
    }
    return problems;
}

/**
 * True when the values being entered disagree with a sibling row's COA for the same lot. Only
 * fields the user has actually filled are compared, so a partially typed COA does not warn until
 * it contradicts the sibling.
 */
export function coaDiffersFromSibling(entered: CoaValues, sibling: CoaValues): boolean {
    const assay = toNumberOrNull(entered.assayPercent);
    if (assay !== null && assay !== toNumberOrNull(sibling.assayPercent)) return true;

    if (entered.assayBasis && entered.assayBasis !== sibling.assayBasis) return true;

    const lod = toNumberOrNull(entered.lodPercent);
    if (lod !== null && lod !== toNumberOrNull(sibling.lodPercent)) return true;

    return false;
}
